import React, { useEffect, useState, useRef } from "react";
import BeagleCardLibraryBlock from "./BeagleCardLibraryBlock";
import { marked } from 'marked';
import { sanitizeHtml } from "../../utils/sanitize";
import { Copy, Check, Volume2, Square, ArrowLeft, AlertTriangle, X, Loader2 } from "lucide-react";
import "./StreamViewer.css";
import { ERROR_MESSAGE_TIMEOUT, STORAGE_KEYS } from "../../utils/constants";
import { useTextToSpeech } from "../../hooks/useTextToSpeech.js";
import { getUserDetails } from "../../utils/authUtils";

const MAX_STREAM_RETRIES = 3;
const STREAM_RETRY_BASE_DELAY = 1000;
const STREAM_RETRY_MAX_DELAY = 10000;

/**
 * Component that renders a streaming view of messages (Text or BeagleCards) via Server-Sent Events (SSE).
 */
export default function StreamViewer({ setStartResearch, setStreamUrl, streamUrl, status, heading, setUserSelectedStream, disableTTS = false }) {

  const userDetails = getUserDetails();
  const token = disableTTS ? null : userDetails?.tts_info?.token;
  const region = disableTTS ? null : userDetails?.tts_info?.region;

  const { textToSpeech, ttsState, stopSpeech } = useTextToSpeech(token, region);

  const [extractedTexts, setExtractedTexts] = useState("");
  const [messages, setMessages] = useState([]);
  const shouldClearMessagesRef = useRef(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const queueRef = useRef([]);
  const [userSelected, setUserSelected] = useState(false);

  const processingRef = useRef(false);
  const streamContainerRef = useRef(null);
  const activeRef = useRef(true);
  const statusRef = useRef(status);
  const timersRef = useRef([]);
  const runIdRef = useRef(0);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const eventSourceRef = useRef(null);
  const connectionIdRef = useRef(0);

  const sleep = (ms) => {
    return new Promise((resolve) => {
      const id = setTimeout(() => {
        timersRef.current = timersRef.current.filter(t => t.id !== id);
        resolve();
      }, ms);
      timersRef.current.push({ id, resolve });
    });
  };

  const extractTextFromMessages = () => {
    if (!Array.isArray(messages)) return "";

    const textualContent = messages
      .filter((msg) => msg.type === "text")
      .map((msg) => msg.data?.content)
      .filter((content) => content)
      .join(" ")
      .replace(/[*#_`]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    return textualContent;
  };

  const handleTTS = (e) => {
    if (ttsState === 'playing' || ttsState === 'loading') {
      stopSpeech();
      return;
    }

    try {
      const content = extractTextFromMessages();
      setExtractedTexts(content);

      if (content && content.length > 0) {
        textToSpeech(content, e.currentTarget);
      }
    } catch (error) {
      console.error("Failed to extract messages:", error);
    }
  };

  const streamText = async (fullText, runId = null) => {
    if (!fullText) return;
    const words = fullText.split(/(\s+)/);
    let currentText = "";

    for (const token of words) {
      currentText += token;
      setMessages((prev) => {
        if (!prev || prev.length === 0) return prev;
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        const last = updated[lastIndex] || {};
        updated[lastIndex] = {
          ...last,
          data: { ...(last.data || {}), content: currentText },
        };
        return updated;
      });
      await sleep(15);
      if (runIdRef.current && runIdRef.current !== runId) return;
    }
  };

  const closeStreamCanvas = () => {
    setStartResearch(false);
    setStreamUrl("");
    try { setUserSelectedStream && setUserSelectedStream(false); } catch (e) { console.warn("Failed to reset user selected stream:", e); }
  };

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!streamUrl) return;
    activeRef.current = true;
    shouldClearMessagesRef.current = true;
    queueRef.current = [];
    processingRef.current = false;
    timersRef.current = [];
    retryCountRef.current = 0;
    connectionIdRef.current += 1;
    const thisConnectionId = connectionIdRef.current;

    setMessages([]);

    let token = null;
    try {
      const userDetails = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION));
      token = userDetails?.token;
    } catch (e) { /* localStorage unavailable — proceed without token */ }

    // Security Hotspot: Token passed in URL is an accepted limitation of EventSource API
    // (no custom header support). Backend should issue short-lived streaming tokens.
    let streamUrlWithToken = streamUrl;
    if (token) {
      try {
        const urlObj = new URL(streamUrl, window.location.origin);
        urlObj.searchParams.set('token', token);
        streamUrlWithToken = urlObj.toString();
      } catch (e) {
        if (streamUrl.indexOf('?') === -1) {
          streamUrlWithToken = `${streamUrl}?token=${encodeURIComponent(token)}`;
        } else {
          streamUrlWithToken = `${streamUrl}&token=${encodeURIComponent(token)}`;
        }
      }
    }

    const connectStream = () => {
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch (e) { /* safe to ignore close errors */ }
        eventSourceRef.current = null;
      }

      shouldClearMessagesRef.current = true;
      queueRef.current = [];
      processingRef.current = false;
      setMessages([]);

      const eventSource = new EventSource(streamUrlWithToken);
      eventSourceRef.current = eventSource;

      runIdRef.current += 1;
      const thisRunId = runIdRef.current;

      const processQueue = async () => {
        if (processingRef.current) return;
        processingRef.current = true;

        if (userSelected && statusRef.current === 'running') {
          queueRef.current = [];
          timersRef.current = [];
          setMessages((prev) => {
            if (!prev || prev.length === 0) return prev;
            const lastMsg = prev[prev.length - 1];
            if (lastMsg?.type === 'text' && lastMsg?.data?.content) {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...lastMsg,
                data: { ...lastMsg.data, content: '' }
              };
              streamText(lastMsg.data.content, runIdRef.current);
              return updated;
            }
            return prev;
          });
          processingRef.current = false;
          return;
        }

        while (queueRef.current.length > 0 && activeRef.current && runIdRef.current === thisRunId) {
          const nextMsg = queueRef.current.shift();
          if (runIdRef.current !== thisRunId || !activeRef.current) break;
          if (!nextMsg) continue;

          const shouldStream = statusRef.current === 'running';

          if (nextMsg.type === 'text' && nextMsg.data?.content) {
            if (shouldStream) {
              if (runIdRef.current !== thisRunId || !activeRef.current) break;
              setMessages((prev) => [...prev, { ...nextMsg, data: { ...nextMsg.data, content: '' } }]);
              await streamText(nextMsg.data.content, thisRunId);
              await sleep(200);
            } else {
              setMessages((prev) => [...prev, { ...nextMsg, data: { ...nextMsg.data } }]);
              await sleep(80);
            }
          } else {
            setMessages((prev) => [...prev, nextMsg]);
            await sleep(120);
          }
        }
        processingRef.current = false;
      };

      eventSource.onmessage = (event) => {
        if (!event.data) return;
        if (event.type === 'done' || event.data === "<<END_STREAM>>") {
          eventSource.close();
          eventSourceRef.current = null;
          return;
        }
        try {
          const msg = JSON.parse(event.data);
          if (shouldClearMessagesRef.current) {
            setMessages([]);
            shouldClearMessagesRef.current = false;
          }
          if (!activeRef.current) return;
          retryCountRef.current = 0;
          queueRef.current.push(msg);
          processQueue();
        } catch (err) {
          setErrorMessage('Error parsing stream data');
          console.error('Failed to parse stream message', err, event.data);
        }
      };

      eventSource.onerror = (err) => {
        console.error("Stream error:", err);
        eventSource.close();
        eventSourceRef.current = null;

        if (!activeRef.current) return;
        if (connectionIdRef.current !== thisConnectionId) return;

        if (retryCountRef.current < MAX_STREAM_RETRIES) {
          const delay = Math.min(
            STREAM_RETRY_BASE_DELAY * Math.pow(2, retryCountRef.current),
            STREAM_RETRY_MAX_DELAY
          );
          retryCountRef.current++;
          retryTimerRef.current = setTimeout(() => {
            if (activeRef.current && connectionIdRef.current === thisConnectionId) {
              connectStream();
            }
          }, delay);
        } else {
          setErrorMessage('Stream connection failed. Please try again.');
        }
      };
    };

    connectStream();

    return () => {
      activeRef.current = false;
      queueRef.current = [];
      processingRef.current = false;
      timersRef.current.forEach(({ id, resolve }) => {
        clearTimeout(id);
        resolve();
      });
      timersRef.current = [];
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch (e) { /* safe to ignore close errors */ }
        eventSourceRef.current = null;
      }
    };
  }, [streamUrl]);

  useEffect(() => {
    if (!errorMessage) return;
    const dismissTimer = setTimeout(() => setErrorMessage(''), ERROR_MESSAGE_TIMEOUT);
    return () => clearTimeout(dismissTimer);
  }, [errorMessage]);

  const copyToClipboard = async (iconElement) => {
    try {
      const parentElement = iconElement.closest('.stream-viewer-container') || iconElement.parentElement;
      const cloned = parentElement.cloneNode(true);

      cloned.querySelectorAll('.stream-action-btn').forEach(i => i.remove());

      const html = cloned.innerHTML;
      const text = cloned.innerText || cloned.textContent || '';

      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([text], { type: 'text/plain' })
            })
          ]);
        } catch (err) {
          await navigator.clipboard.writeText(text);
        }
      } else {
        await navigator.clipboard.writeText(text);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <>
      {/* Error Banner */}
      {errorMessage && (
        <div className="flex items-center justify-between px-4 py-3 text-sm font-medium text-red-700 bg-red-50 border-b"
          style={{ borderColor: 'var(--border-color)' }}>
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="p-1 rounded hover:bg-red-100 transition-colors">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Sticky Header */}
      {heading && (
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-2 sm:px-3 md:px-4 py-2"
          style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}
        >
          <h2 className="text-sm font-semibold m-0 truncate min-w-0 flex-1 mr-2" style={{ color: 'var(--text-primary)' }}>
            {heading}
          </h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={(e) => copyToClipboard(streamContainerRef.current || e.currentTarget)}
              className="stream-action-btn p-1.5 rounded-md transition-colors hover:bg-black/5"
              style={{ color: 'var(--text-tertiary)' }}
              title="Copy content"
            >
              {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
            </button>
            <button
              onClick={handleTTS}
              className="stream-action-btn p-1.5 rounded-md transition-all"
              style={{
                color: ttsState === 'playing' ? '#EF4444'
                     : ttsState === 'loading' ? 'var(--text-primary)'
                     : 'var(--text-tertiary)',
                cursor: messages.length === 0 ? 'not-allowed' : 'pointer',
                opacity: messages.length === 0 ? 0.5 : 1,
                backgroundColor: ttsState === 'playing' ? 'rgba(239, 68, 68, 0.1)'
                               : ttsState === 'loading' ? 'rgba(0, 0, 0, 0.05)'
                               : 'transparent',
              }}
              disabled={messages.length === 0}
              title={ttsState === 'playing' ? 'Stop narration'
                   : ttsState === 'loading' ? 'Starting narration...'
                   : 'Narrate'}
            >
              {ttsState === 'playing' ? <Square size={16} fill="currentColor" />
               : ttsState === 'loading' ? <Loader2 size={16} className="animate-spin" />
               : <Volume2 size={16} />}
            </button>
            <button
              id="back-to-chat-button"
              onClick={() => closeStreamCanvas()}
              aria-label="Back to chat"
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-white transition-colors hover:opacity-90 ml-1"
              style={{ background: 'var(--text-primary)' }}
            >
              <ArrowLeft size={14} />
              Chat
            </button>
          </div>
        </div>
      )}

      <div className="p-2 sm:p-3 md:p-4 space-y-4">
        {/* Stream Content */}
        <div className="stream-viewer-container relative" ref={streamContainerRef}>

          {/* Messages */}
          {messages.map((msg, i) => {
            switch (msg.type) {
              case "text":
                return (
                  <div
                    key={i}
                    className={`canvas-text-block ${status}`}
                    data-msg-index={i}
                  >
                    <div
                      className="markdown-content"
                      style={{ color: 'var(--text-primary)' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(marked.parse(
                        (msg?.data?.content || '')
                          .replace(/[\u2460-\u2473\u24EA]/g, '•')
                          .replace(/[\u2776-\u277F]/g, '•')
                          .replace(/[\u2780-\u2789]/g, '•')
                          .replace(/[\u278A-\u2793]/g, '•')
                          .replace(/[0-9]\uFE0F?\u20E3/g, '•')
                      )).replace(/<ol\b[^>]*>/gi, '<ul>').replace(/<\/ol>/gi, '</ul>') }}
                    />
                  </div>
                );
              case "beagle_cards":
                return <BeagleCardLibraryBlock key={i} message={msg.data} />;
              case "error":
                return (
                  <div
                    key={i}
                    className="canvas-error-block flex items-start gap-3 px-5 py-4 mx-5 my-3 rounded-md text-sm"
                    style={{
                      background: 'var(--error-bg)',
                      border: '1px solid var(--error-border)',
                      borderLeft: '4px solid #ef4444',
                      color: '#991b1b',
                    }}
                    data-msg-index={i}
                  >
                    <AlertTriangle size={16} className="shrink-0 mt-0.5 text-red-500" />
                    <span>{msg?.data?.content || "An error occurred"}</span>
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>

        {/* Loading Skeleton */}
        {messages.length === 0 && (
          <div className="space-y-4 animate-pulse px-5">
            <div className="space-y-2">
              <div className="h-4 rounded-full w-full" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[90%]" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[80%]" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[85%]" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[70%]" style={{ background: 'var(--bg-tertiary)' }} />
            </div>
            <div className="space-y-2">
              <div className="h-4 rounded-full w-[95%]" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[75%]" style={{ background: 'var(--bg-tertiary)' }} />
              <div className="h-4 rounded-full w-[60%]" style={{ background: 'var(--bg-tertiary)' }} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
